import { useEffect, useRef, useState } from 'react'
import { supabase } from './supabaseClient'
import './Settings.css'

export default function Settings({ user, onReplayTour }) {
  useEffect(() => { document.title = 'Settings — CommitHQ' }, [])

  // ── Account ───────────────────────────────────────────────────────
  const [displayName, setDisplayName]   = useState('')
  const [nameSaving, setNameSaving]     = useState(false)
  const [nameError, setNameError]       = useState(null)
  const [profileLoading, setProfileLoading] = useState(true)

  // ── Location ──────────────────────────────────────────────────────
  const [zipCode, setZipCode]           = useState('')
  const [zipSaving, setZipSaving]       = useState(false)
  const [zipError, setZipError]         = useState(null)

  // ── Password ──────────────────────────────────────────────────────
  const [newPassword, setNewPassword]   = useState('')
  const [confirmPw, setConfirmPw]       = useState('')
  const [pwSaving, setPwSaving]         = useState(false)
  const [pwError, setPwError]           = useState(null)

  // ── Delete ────────────────────────────────────────────────────────
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleting, setDeleting]               = useState(false)
  const [deleteError, setDeleteError]         = useState(null)

  // ── Toast ─────────────────────────────────────────────────────────
  const [toast, setToast] = useState(null)

  // ── Load profile ──────────────────────────────────────────────────

  useEffect(() => {
    async function loadProfile() {
      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single()
      if (data?.display_name) setDisplayName(data.display_name)
      if (data?.zip_code) setZipCode(data.zip_code)
      setProfileLoading(false)
    }
    loadProfile()
  }, [user.id])

  // ── Handlers ─────────────────────────────────────────────────────

  async function handleSaveName() {
    setNameSaving(true)
    setNameError(null)
    const { error } = await supabase
      .from('profiles')
      .upsert(
        { id: user.id, display_name: displayName.trim(), updated_at: new Date().toISOString() },
        { onConflict: 'id' }
      )
    if (error) {
      setNameError(error.message)
    } else {
      setToast('Display name saved!')
    }
    setNameSaving(false)
  }

  async function handleSaveZip() {
    setZipSaving(true)
    setZipError(null)
    const zip = zipCode.trim()

    // Step 1: save zip_code to profiles
    const { error: zipErr } = await supabase
      .from('profiles')
      .upsert(
        { id: user.id, zip_code: zip || null, updated_at: new Date().toISOString() },
        { onConflict: 'id' }
      )

    if (zipErr) {
      setZipError(zipErr.message)
      setZipSaving(false)
      return
    }

    // Step 2: geocode zip via Zippopotam.us and save coords
    if (zip.length === 5) {
      try {
        const response = await fetch(`https://api.zippopotam.us/us/${zip}`)
        const result   = await response.json()
        const lat = parseFloat(result.places[0].latitude)
        const lng = parseFloat(result.places[0].longitude)

        const { error: coordErr } = await supabase
          .from('profiles')
          .upsert(
            { id: user.id, home_lat: lat, home_lng: lng },
            { onConflict: 'id' }
          )

        if (coordErr) {
          setZipError(coordErr.message)
          setZipSaving(false)
          return
        }
      } catch (err) {
        console.log('[Settings] zip geocoding error:', err)
      }
    }

    setToast('Location saved!')
    setZipSaving(false)
  }

  async function handleChangePassword(e) {
    e.preventDefault()
    setPwError(null)

    if (newPassword.length < 8) {
      setPwError('Password must be at least 8 characters.')
      return
    }
    if (newPassword !== confirmPw) {
      setPwError('Passwords do not match.')
      return
    }

    setPwSaving(true)
    const { error } = await supabase.auth.updateUser({ password: newPassword })
    if (error) {
      setPwError(error.message)
    } else {
      setNewPassword('')
      setConfirmPw('')
      setToast('Password updated!')
    }
    setPwSaving(false)
  }

  async function handleDeleteAccount() {
    setDeleting(true)
    setDeleteError(null)

    // Delete user data rows (best-effort; auth user deletion requires server-side admin call)
    await Promise.all([
      supabase.from('pipeline_schools').delete().eq('user_id', user.id),
      supabase.from('coaches').delete().eq('user_id', user.id),
      supabase.from('profiles').delete().eq('id', user.id),
    ])

    // Sign out — the Auth component will handle showing the login page
    await supabase.auth.signOut()
  }

  // ── Render ────────────────────────────────────────────────────────

  return (
    <div className="settings-page">

      {/* ── Account ── */}
      <section className="settings-section">
        <h2 className="settings-section-title">Account</h2>

        {/* Email (read-only) */}
        <div className="settings-field">
          <label className="settings-label">Email</label>
          <input
            className="settings-input settings-input-readonly"
            type="email"
            value={user.email}
            readOnly
          />
        </div>

        {/* Display name */}
        <div className="settings-field">
          <label className="settings-label">Display Name</label>
          <div className="settings-input-row">
            <input
              className="settings-input"
              type="text"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              placeholder={profileLoading ? 'Loading…' : 'Your name'}
              disabled={profileLoading}
              onKeyDown={e => { if (e.key === 'Enter') handleSaveName() }}
            />
            <button
              className="btn-settings-save"
              onClick={handleSaveName}
              disabled={nameSaving || profileLoading}
            >
              {nameSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
          {nameError && <p className="settings-error">{nameError}</p>}
        </div>

        {/* Replay Tour */}
        {onReplayTour && (
          <div className="settings-field">
            <label className="settings-label">App Tour</label>
            <p className="settings-field-hint">Replay the guided tour of CommitHQ.</p>
            <button className="btn-settings-secondary" onClick={onReplayTour}>
              Replay Tour
            </button>
          </div>
        )}
      </section>

      {/* ── Location ── */}
      <section className="settings-section">
        <h2 className="settings-section-title">Location</h2>
        <div className="settings-field">
          <label className="settings-label">Home Zip Code</label>
          <p className="settings-field-hint">Used to calculate distances to college programs.</p>
          <div className="settings-input-row">
            <input
              className="settings-input settings-input-zip"
              type="text"
              value={zipCode}
              onChange={e => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
              placeholder="e.g. 78701"
              disabled={profileLoading}
              onKeyDown={e => { if (e.key === 'Enter') handleSaveZip() }}
            />
            <button
              className="btn-settings-save"
              onClick={handleSaveZip}
              disabled={zipSaving || profileLoading}
            >
              {zipSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
          {zipError && <p className="settings-error">{zipError}</p>}
        </div>
      </section>

      {/* ── Password ── */}
      <section className="settings-section">
        <h2 className="settings-section-title">Password</h2>
        <form onSubmit={handleChangePassword}>
          <div className="settings-field">
            <label className="settings-label">New Password</label>
            <input
              className="settings-input"
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder="Min. 8 characters"
              autoComplete="new-password"
            />
          </div>
          <div className="settings-field" style={{ marginTop: 12 }}>
            <label className="settings-label">Confirm New Password</label>
            <input
              className="settings-input"
              type="password"
              value={confirmPw}
              onChange={e => setConfirmPw(e.target.value)}
              placeholder="Re-enter new password"
              autoComplete="new-password"
            />
          </div>
          {pwError && <p className="settings-error" style={{ marginTop: 10 }}>{pwError}</p>}
          <button
            className="btn-settings-save"
            type="submit"
            disabled={pwSaving}
            style={{ marginTop: 14 }}
          >
            {pwSaving ? 'Updating…' : 'Update Password'}
          </button>
        </form>
      </section>

      {/* ── Danger Zone ── */}
      <section className="settings-section settings-danger-zone">
        <h2 className="settings-section-title settings-danger-title">Danger Zone</h2>
        <p className="settings-danger-desc">
          Deleting your account will remove all your pipeline schools, coaches, and profile data. This action cannot be undone.
        </p>
        <button className="btn-delete-account" onClick={() => setShowDeleteModal(true)}>
          Delete Account
        </button>
      </section>

      {/* ── Delete confirm modal ── */}
      {showDeleteModal && (
        <div className="settings-modal-overlay" onClick={() => { if (!deleting) setShowDeleteModal(false) }}>
          <div className="settings-modal" onClick={e => e.stopPropagation()}>
            <div className="settings-modal-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </div>
            <h3 className="settings-modal-title">Delete Account</h3>
            <p className="settings-modal-body">
              Are you sure? This will permanently delete your account and all your data. <strong>This cannot be undone.</strong>
            </p>
            <p className="settings-modal-note">
              Note: you will be signed out immediately. To complete auth deletion, please contact support.
            </p>
            {deleteError && <p className="settings-error">{deleteError}</p>}
            <div className="settings-modal-actions">
              <button
                className="btn-settings-cancel"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="btn-confirm-delete"
                onClick={handleDeleteAccount}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Yes, Delete My Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && <SettingsToast message={toast} onDone={() => setToast(null)} />}
    </div>
  )
}

function SettingsToast({ message, onDone }) {
  const t = useRef(null)
  useEffect(() => {
    t.current = setTimeout(onDone, 3000)
    return () => clearTimeout(t.current)
  }, [onDone])
  return (
    <div className="settings-toast">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
        <polyline points="22 4 12 14.01 9 11.01"/>
      </svg>
      {message}
    </div>
  )
}
